import { ageForGrade, gradeLabel, type School } from "./data";
import { formatPhoneInput, phoneDigits } from "./phone";

export const PERMISSION_FORM_YEAR = "2026-2027";
export const PERMISSION_FORM_NAME = "LIFT Permission Form 2026-2027";
export const PERMISSION_FORM_INTRO =
  "This is the LIFT permission form for 2026-2027 programming. Fill it once for each student. The student is saved on this family account.";
export const PERMISSION_FORM_INTRO_ES =
  "Este es el formulario de permiso de LIFT para la programación 2026-2027. Llénele una vez por cada estudiante. El estudiante queda guardado en esta cuenta familiar.";

export const SCHOOL_FORM_ORDER: School[] = [
  "Glen Lake",
  "Suttons Bay",
  "Northport",
  "Leland",
];

export const SCHOOL_FORM_LABELS: Record<School, string> = {
  "Glen Lake": "Glen Lake Community Schools",
  "Suttons Bay": "Suttons Bay Public School",
  Northport: "Northport Public School",
  Leland: "Leland Public School",
};

export const GENDERS = [
  { id: "male", en: "Male", es: "Masculino" },
  { id: "female", en: "Female", es: "Femenino" },
  { id: "transgender", en: "Transgender", es: "Transgénero" },
  { id: "non-binary", en: "Non-Binary", es: "No binario" },
  { id: "prefer-not", en: "Prefer not to say", es: "Prefiero no decirlo" },
  { id: "not-listed", en: "Gender not listed", es: "Género no listado" },
] as const;

export const RACES = [
  { id: "african-american", en: "African American", es: "Afroamericano" },
  { id: "asian", en: "Asian", es: "Asiático" },
  { id: "caucasian", en: "Caucasian", es: "Caucásico" },
  { id: "hispanic-latino", en: "Hispanic or Latino", es: "Hispano o latino" },
  { id: "native-american", en: "Native American", es: "Nativo americano" },
] as const;

export const RELATIONSHIPS = [
  { id: "father", en: "Father", es: "Padre" },
  { id: "mother", en: "Mother", es: "Madre" },
  { id: "grandfather", en: "Grandfather", es: "Abuelo" },
  { id: "grandmother", en: "Grandmother", es: "Abuela" },
  { id: "brother", en: "Brother", es: "Hermano" },
  { id: "sister", en: "Sister", es: "Hermana" },
  { id: "uncle", en: "Uncle", es: "Tío" },
  { id: "aunt", en: "Aunt", es: "Tía" },
  { id: "legal-guardian", en: "Legal Guardian", es: "Tutor legal" },
  { id: "other", en: "Other", es: "Otro" },
] as const;

export const HELP_INTERESTS = [
  { id: "volunteering", en: "Volunteering", es: "Voluntariado" },
  { id: "special-events", en: "Special Events", es: "Eventos especiales" },
  { id: "donating-funds", en: "Donating Funds", es: "Donar fondos" },
  { id: "wish-list", en: "Donating Wish-List Items", es: "Donar artículos de la lista de deseos" },
  { id: "snacks", en: "Donating Snacks", es: "Donar meriendas" },
] as const;

export type GenderId = (typeof GENDERS)[number]["id"];
export type RaceId = (typeof RACES)[number]["id"];
export type RelationshipId = (typeof RELATIONSHIPS)[number]["id"];
export type HelpInterestId = (typeof HELP_INTERESTS)[number]["id"];
export type YesNo = "yes" | "no";
export type AgreeChoice = "agree" | "meeting";

export type NamedContact = {
  firstName: string;
  lastName: string;
  phone: string;
  relationship: string;
};

export type GuardianAnswers = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationship: RelationshipId | "";
  address: string;
  help: HelpInterestId[];
};

export type AdditionalGuardianAnswers = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationship: RelationshipId | "";
  address: string;
};

export type PermissionAnswers = {
  student: {
    firstName: string;
    lastName: string;
    email: string;
    grade: number | "";
    birthday: string;
    phone: string;
    address: string;
    gender: GenderId | "";
    races: RaceId[];
    school: School | "";
    pickupPeople: string;
    walkHome: YesNo | "";
  };
  guardian: GuardianAnswers;
  additionalGuardian: AdditionalGuardianAnswers;
  emergency1: NamedContact;
  emergency2: NamedContact;
  health: {
    behavioral: YesNo | "";
    behavioralDetail: string;
    medical: YesNo | "";
    medicalDetail: string;
    otcMeds: YesNo | "";
    emergencyCare: YesNo | "";
    physicianName: string;
    physicianPhone: string;
    hospital: string;
    insuranceProvider: string;
    policyNumber: string;
  };
  policies: {
    conduct: AgreeChoice | "";
    behavior: AgreeChoice | "";
    dismissal: AgreeChoice | "";
    media: YesNo | "";
  };
  liability: {
    participate: boolean;
    transport: boolean;
    risks: boolean;
    release: boolean;
  };
};

export type PermissionIssue = {
  fieldId: string;
  en: string;
  es: string;
};

type Locale = "en" | "es";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emptyPermission(): PermissionAnswers {
  return {
    student: {
      firstName: "",
      lastName: "",
      email: "",
      grade: "",
      birthday: "",
      phone: "",
      address: "",
      gender: "",
      races: [],
      school: "",
      pickupPeople: "",
      walkHome: "",
    },
    guardian: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      relationship: "",
      address: "",
      help: [],
    },
    additionalGuardian: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      relationship: "",
      address: "",
    },
    emergency1: { firstName: "", lastName: "", phone: "", relationship: "" },
    emergency2: { firstName: "", lastName: "", phone: "", relationship: "" },
    health: {
      behavioral: "",
      behavioralDetail: "",
      medical: "",
      medicalDetail: "",
      otcMeds: "",
      emergencyCare: "",
      physicianName: "",
      physicianPhone: "",
      hospital: "",
      insuranceProvider: "",
      policyNumber: "",
    },
    policies: { conduct: "", behavior: "", dismissal: "", media: "" },
    liability: { participate: false, transport: false, risks: false, release: false },
  };
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function yesNo(value: unknown): YesNo | "" {
  return value === "yes" || value === "no" ? value : "";
}

function agree(value: unknown): AgreeChoice | "" {
  return value === "agree" || value === "meeting" ? value : "";
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | "" {
  return typeof value === "string" && allowed.includes(value as T) ? (value as T) : "";
}

function manyOf<T extends string>(value: unknown, allowed: readonly T[]): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is T => typeof item === "string" && allowed.includes(item as T));
}

function contact(value: unknown): NamedContact {
  const row = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  return {
    firstName: text(row.firstName),
    lastName: text(row.lastName),
    phone: text(row.phone),
    relationship: text(row.relationship),
  };
}

export function normalizePermission(value: unknown): PermissionAnswers {
  const blank = emptyPermission();
  if (!value || typeof value !== "object") return blank;
  const row = value as Record<string, unknown>;
  const student = row.student && typeof row.student === "object"
    ? (row.student as Record<string, unknown>)
    : {};
  const guardian = row.guardian && typeof row.guardian === "object"
    ? (row.guardian as Record<string, unknown>)
    : {};
  const additional = row.additionalGuardian && typeof row.additionalGuardian === "object"
    ? (row.additionalGuardian as Record<string, unknown>)
    : {};
  const health = row.health && typeof row.health === "object"
    ? (row.health as Record<string, unknown>)
    : {};
  const policies = row.policies && typeof row.policies === "object"
    ? (row.policies as Record<string, unknown>)
    : {};
  const liability = row.liability && typeof row.liability === "object"
    ? (row.liability as Record<string, unknown>)
    : {};
  const grade = typeof student.grade === "number" && gradeLabel(student.grade) !== String(student.grade)
    ? student.grade
    : typeof student.grade === "number" && student.grade >= -3 && student.grade <= 12
      ? student.grade
      : "";
  const school = oneOf(student.school, SCHOOL_FORM_ORDER);
  return {
    student: {
      firstName: text(student.firstName),
      lastName: text(student.lastName),
      email: text(student.email),
      grade,
      birthday: text(student.birthday),
      phone: text(student.phone),
      address: text(student.address),
      gender: oneOf(student.gender, GENDERS.map((item) => item.id)),
      races: manyOf(student.races, RACES.map((item) => item.id)),
      school,
      pickupPeople: text(student.pickupPeople),
      walkHome: yesNo(student.walkHome),
    },
    guardian: {
      firstName: text(guardian.firstName),
      lastName: text(guardian.lastName),
      email: text(guardian.email),
      phone: text(guardian.phone),
      relationship: oneOf(guardian.relationship, RELATIONSHIPS.map((item) => item.id)),
      address: text(guardian.address),
      help: manyOf(guardian.help, HELP_INTERESTS.map((item) => item.id)),
    },
    additionalGuardian: {
      firstName: text(additional.firstName),
      lastName: text(additional.lastName),
      email: text(additional.email),
      phone: text(additional.phone),
      relationship: oneOf(additional.relationship, RELATIONSHIPS.map((item) => item.id)),
      address: text(additional.address),
    },
    emergency1: contact(row.emergency1),
    emergency2: contact(row.emergency2),
    health: {
      behavioral: yesNo(health.behavioral),
      behavioralDetail: text(health.behavioralDetail),
      medical: yesNo(health.medical),
      medicalDetail: text(health.medicalDetail),
      otcMeds: yesNo(health.otcMeds),
      emergencyCare: yesNo(health.emergencyCare),
      physicianName: text(health.physicianName),
      physicianPhone: text(health.physicianPhone),
      hospital: text(health.hospital),
      insuranceProvider: text(health.insuranceProvider),
      policyNumber: text(health.policyNumber),
    },
    policies: {
      conduct: agree(policies.conduct),
      behavior: agree(policies.behavior),
      dismissal: agree(policies.dismissal),
      media: yesNo(policies.media),
    },
    liability: {
      participate: liability.participate === true,
      transport: liability.transport === true,
      risks: liability.risks === true,
      release: liability.release === true,
    },
  };
}

function filled(value: string): boolean {
  return value.trim().length > 0;
}

function validEmail(value: string): boolean {
  return EMAIL.test(value.trim());
}

function validPhone(value: string): boolean {
  return phoneDigits(value).length === 10;
}

function issue(fieldId: string, en: string, es: string): PermissionIssue {
  return { fieldId, en, es };
}

export function permissionIssue(value: PermissionAnswers): PermissionIssue | null {
  const student = value.student;
  if (!filled(student.firstName) || !filled(student.lastName)) {
    return issue(
      filled(student.firstName) ? "student-last" : "student-first",
      "Add the student's first and last name.",
      "Agregue el nombre y el apellido del estudiante.",
    );
  }
  if (!validEmail(student.email)) {
    return issue(
      "student-email",
      "Add a valid student email.",
      "Agregue un correo válido del estudiante.",
    );
  }
  if (student.grade === "") {
    return issue("student-grade", "Choose a grade.", "Elija un grado.");
  }
  if (!filled(student.birthday)) {
    return issue(
      "student-birthday",
      "Add the student's date of birth.",
      "Agregue la fecha de nacimiento del estudiante.",
    );
  }
  if (filled(student.phone) && !validPhone(student.phone)) {
    return issue(
      "student-phone",
      "Student phone needs a 10-digit number, or leave it blank.",
      "El teléfono del estudiante necesita 10 dígitos, o déjelo en blanco.",
    );
  }
  if (!filled(student.address)) {
    return issue("student-address", "Add the student's address.", "Agregue la dirección del estudiante.");
  }
  if (!student.gender) {
    return issue("student-gender", "Choose a gender.", "Elija un género.");
  }
  if (!student.school) {
    return issue("student-school", "Choose the school the student attends.", "Elija la escuela del estudiante.");
  }
  if (!filled(student.pickupPeople)) {
    return issue(
      "student-pickup",
      "List who may pick the student up, with each person's relationship.",
      "Indique quién puede recoger al estudiante y el parentesco de cada persona.",
    );
  }
  if (!student.walkHome) {
    return issue(
      "student-walk",
      "Say whether the student may walk home.",
      "Indique si el estudiante puede caminar a casa.",
    );
  }

  const guardian = value.guardian;
  if (!filled(guardian.firstName) || !filled(guardian.lastName)) {
    return issue(
      filled(guardian.firstName) ? "guardian-last" : "guardian-first",
      "Add the parent or guardian's first and last name.",
      "Agregue el nombre y el apellido del padre o tutor.",
    );
  }
  if (!validEmail(guardian.email)) {
    return issue(
      "guardian-email",
      "Add a valid parent or guardian email.",
      "Agregue un correo válido del padre o tutor.",
    );
  }
  if (!validPhone(guardian.phone)) {
    return issue(
      "guardian-phone",
      "Add a 10-digit parent or guardian phone number.",
      "Agregue un teléfono de 10 dígitos del padre o tutor.",
    );
  }
  if (!guardian.relationship) {
    return issue(
      "guardian-relationship",
      "Choose the parent or guardian's relationship to the student.",
      "Elija el parentesco del padre o tutor con el estudiante.",
    );
  }

  const extra = value.additionalGuardian;
  const extraStarted = [extra.firstName, extra.lastName, extra.email, extra.phone, extra.relationship, extra.address]
    .some((item) => filled(item));
  if (extraStarted) {
    if (!filled(extra.firstName) || !filled(extra.lastName)) {
      return issue(
        "additional-first",
        "Finish the additional parent or guardian's name, or clear that section.",
        "Complete el nombre del padre o tutor adicional, o borre esa sección.",
      );
    }
    if (filled(extra.email) && !validEmail(extra.email)) {
      return issue(
        "additional-email",
        "The additional parent or guardian email is not valid.",
        "El correo del padre o tutor adicional no es válido.",
      );
    }
    if (!validPhone(extra.phone)) {
      return issue(
        "additional-phone",
        "Add a 10-digit phone for the additional parent or guardian, or clear that section.",
        "Agregue un teléfono de 10 dígitos del padre o tutor adicional, o borre esa sección.",
      );
    }
    if (!extra.relationship) {
      return issue(
        "additional-relationship",
        "Choose the additional parent or guardian's relationship, or clear that section.",
        "Elija el parentesco del padre o tutor adicional, o borre esa sección.",
      );
    }
  }

  const emergencyIssue = (contactRow: NamedContact, prefix: string, which: string, whichEs: string) => {
    if (!filled(contactRow.firstName) || !filled(contactRow.lastName)) {
      return issue(
        `${prefix}-first`,
        `Add emergency contact ${which}'s first and last name.`,
        `Agregue el nombre y el apellido del contacto de emergencia ${whichEs}.`,
      );
    }
    if (!validPhone(contactRow.phone)) {
      return issue(
        `${prefix}-phone`,
        `Add a 10-digit phone for emergency contact ${which}.`,
        `Agregue un teléfono de 10 dígitos del contacto de emergencia ${whichEs}.`,
      );
    }
    if (!filled(contactRow.relationship)) {
      return issue(
        `${prefix}-relationship`,
        `Add emergency contact ${which}'s relationship to the student.`,
        `Agregue el parentesco del contacto de emergencia ${whichEs} con el estudiante.`,
      );
    }
    return null;
  };
  const firstEmergency = emergencyIssue(value.emergency1, "emergency1", "1", "1");
  if (firstEmergency) return firstEmergency;
  const secondEmergency = emergencyIssue(value.emergency2, "emergency2", "2", "2");
  if (secondEmergency) return secondEmergency;

  const health = value.health;
  if (!health.behavioral) {
    return issue(
      "health-behavioral",
      "Answer whether your child has behavioral or emotional needs.",
      "Responda si su hijo tiene necesidades conductuales o emocionales.",
    );
  }
  if (health.behavioral === "yes" && !filled(health.behavioralDetail)) {
    return issue(
      "health-behavioral-detail",
      "Describe the behavioral or emotional needs.",
      "Describa las necesidades conductuales o emocionales.",
    );
  }
  if (!health.medical) {
    return issue(
      "health-medical",
      "Answer whether your student has medical conditions staff should know about.",
      "Responda si el estudiante tiene condiciones médicas que el personal deba conocer.",
    );
  }
  if (health.medical === "yes" && !filled(health.medicalDetail)) {
    return issue(
      "health-medical-detail",
      "Describe the medical conditions.",
      "Describa las condiciones médicas.",
    );
  }
  if (!health.otcMeds) {
    return issue(
      "health-otc",
      "Choose whether LIFT may give prescription or over-the-counter medication.",
      "Elija si LIFT puede administrar medicamentos recetados o de venta libre.",
    );
  }
  if (!health.emergencyCare) {
    return issue(
      "health-emergency-care",
      "Choose whether LIFT may seek emergency medical care.",
      "Elija si LIFT puede buscar atención médica de emergencia.",
    );
  }
  if (!filled(health.physicianName)) {
    return issue(
      "health-physician",
      "Add the primary care physician's name.",
      "Agregue el nombre del médico de cabecera.",
    );
  }
  if (!validPhone(health.physicianPhone)) {
    return issue(
      "health-physician-phone",
      "Add a 10-digit physician phone number.",
      "Agregue un teléfono de 10 dígitos del médico.",
    );
  }

  if (!value.policies.conduct) {
    return issue("policy-conduct", "Answer the code of conduct agreement.", "Responda el acuerdo del código de conducta.");
  }
  if (!value.policies.behavior) {
    return issue("policy-behavior", "Answer the behavior response agreement.", "Responda el acuerdo del proceso de conducta.");
  }
  if (!value.policies.dismissal) {
    return issue("policy-dismissal", "Answer the dismissal policy agreement.", "Responda el acuerdo de la política de salida.");
  }
  if (!value.policies.media) {
    return issue("policy-media", "Answer the media release.", "Responda la autorización de medios.");
  }
  if (!value.liability.participate || !value.liability.transport || !value.liability.risks || !value.liability.release) {
    return issue(
      !value.liability.participate
        ? "liability-participate"
        : !value.liability.transport
          ? "liability-transport"
          : !value.liability.risks
            ? "liability-risks"
            : "liability-release",
      "Check each participation and liability agreement.",
      "Marque cada acuerdo de participación y responsabilidad.",
    );
  }
  return null;
}

type RosterSeed = {
  given: string;
  family: string;
  email: string;
  school: School;
  grade: number;
  birthday: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  emergencyName: string;
  emergencyPhone: string;
};

function splitName(value: string): { firstName: string; lastName: string } {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
}

/** Starts an edit from the saved form, filling any blank identity fields from the roster. */
export function permissionForEdit(student: RosterSeed, saved?: PermissionAnswers): PermissionAnswers {
  const next = saved ? normalizePermission(saved) : emptyPermission();
  const guardian = splitName(student.guardianName);
  const emergency = splitName(student.emergencyName);
  return {
    ...next,
    student: {
      ...next.student,
      firstName: next.student.firstName || student.given,
      lastName: next.student.lastName || student.family,
      email: next.student.email || student.email,
      grade: next.student.grade === "" ? student.grade : next.student.grade,
      birthday: next.student.birthday || student.birthday,
      school: next.student.school || student.school,
    },
    guardian: {
      ...next.guardian,
      firstName: next.guardian.firstName || guardian.firstName,
      lastName: next.guardian.lastName || guardian.lastName,
      email: next.guardian.email || student.guardianEmail,
      phone: next.guardian.phone || formatPhoneInput(student.guardianPhone),
    },
    emergency1: {
      ...next.emergency1,
      firstName: next.emergency1.firstName || emergency.firstName,
      lastName: next.emergency1.lastName || emergency.lastName,
      phone: next.emergency1.phone || formatPhoneInput(student.emergencyPhone),
    },
  };
}

export function permissionCore(value: PermissionAnswers) {
  if (value.student.school === "" || value.student.grade === "") {
    throw new Error("Permission form is incomplete.");
  }
  const grade = value.student.grade;
  return {
    given: value.student.firstName.trim(),
    family: value.student.lastName.trim(),
    email: value.student.email.trim(),
    school: value.student.school,
    grade,
    age: ageForGrade(grade),
    birthday: value.student.birthday,
    guardianName: `${value.guardian.firstName} ${value.guardian.lastName}`.trim(),
    guardianEmail: value.guardian.email.trim(),
    guardianPhone: value.guardian.phone.trim(),
    emergencyName: `${value.emergency1.firstName} ${value.emergency1.lastName}`.trim(),
    emergencyPhone: value.emergency1.phone.trim(),
    permissionSlips:
      value.liability.participate &&
      value.liability.transport &&
      value.liability.risks &&
      value.liability.release,
    medical: value.health.emergencyCare === "yes",
    conduct: value.policies.conduct === "agree",
  };
}

/** The three checklist flags the roster still stores beside the full form. */
export function consentFlags(value: PermissionAnswers): {
  permissionSlips: boolean;
  medical: boolean;
  conduct: boolean;
} {
  return {
    permissionSlips:
      value.liability.participate &&
      value.liability.transport &&
      value.liability.risks &&
      value.liability.release,
    medical: value.health.emergencyCare === "yes",
    conduct: value.policies.conduct === "agree",
  };
}

/** True when a submission has the official form, not only the old yes/no checklist. */
export function permissionIsFiled(value: PermissionAnswers | undefined): boolean {
  if (!value) return false;
  return (
    value.student.firstName.trim() !== "" ||
    value.student.address.trim() !== "" ||
    value.health.medical !== "" ||
    value.policies.media !== ""
  );
}

function optionLabel(
  options: readonly { id: string; en: string; es: string }[],
  id: string,
  locale: Locale,
): string {
  const match = options.find((item) => item.id === id);
  if (!match) return "";
  return locale === "es" ? match.es : match.en;
}

function yesNoExport(value: YesNo | ""): string {
  if (value === "yes") return "yes";
  if (value === "no") return "no";
  return "";
}

function agreeExport(value: AgreeChoice | ""): string {
  if (value === "agree") return "agree";
  if (value === "meeting") return "meeting requested";
  return "";
}

export const PERMISSION_EXPORT_HEADERS = [
  "Student phone",
  "Address",
  "Gender",
  "Race / ethnicity",
  "May leave with",
  "Walk home",
  "Guardian relationship",
  "Guardian address",
  "Help with",
  "Additional guardian",
  "Additional guardian email",
  "Additional guardian phone",
  "Additional guardian relationship",
  "Additional guardian address",
  "Emergency 1 relationship",
  "Emergency 2",
  "Emergency 2 phone",
  "Emergency 2 relationship",
  "Behavioral needs",
  "Behavioral detail",
  "Medical conditions",
  "Medical detail",
  "OTC medication",
  "Emergency medical care",
  "Physician",
  "Physician phone",
  "Preferred hospital",
  "Insurance",
  "Policy number",
  "Code of conduct",
  "Behavior response",
  "Dismissal policy",
  "Media release",
  "Participation permission",
  "Transportation permission",
  "Assumption of risk",
  "Liability release",
];

export function permissionExportValues(value: PermissionAnswers | undefined): string[] {
  const row = value ?? emptyPermission();
  const answered = filled(row.student.firstName) || filled(row.student.address);
  const checked = (flag: boolean) => (answered ? (flag ? "yes" : "no") : "");
  const extraName = `${row.additionalGuardian.firstName} ${row.additionalGuardian.lastName}`.trim();
  const emergency2 = `${row.emergency2.firstName} ${row.emergency2.lastName}`.trim();
  return [
    row.student.phone,
    row.student.address,
    optionLabel(GENDERS, row.student.gender, "en"),
    row.student.races.map((id) => optionLabel(RACES, id, "en")).join("; "),
    row.student.pickupPeople,
    yesNoExport(row.student.walkHome),
    optionLabel(RELATIONSHIPS, row.guardian.relationship, "en"),
    row.guardian.address,
    row.guardian.help.map((id) => optionLabel(HELP_INTERESTS, id, "en")).join("; "),
    extraName,
    row.additionalGuardian.email,
    row.additionalGuardian.phone,
    optionLabel(RELATIONSHIPS, row.additionalGuardian.relationship, "en"),
    row.additionalGuardian.address,
    row.emergency1.relationship,
    emergency2,
    row.emergency2.phone,
    row.emergency2.relationship,
    yesNoExport(row.health.behavioral),
    row.health.behavioralDetail,
    yesNoExport(row.health.medical),
    row.health.medicalDetail,
    yesNoExport(row.health.otcMeds),
    yesNoExport(row.health.emergencyCare),
    row.health.physicianName,
    row.health.physicianPhone,
    row.health.hospital,
    row.health.insuranceProvider,
    row.health.policyNumber,
    agreeExport(row.policies.conduct),
    agreeExport(row.policies.behavior),
    agreeExport(row.policies.dismissal),
    yesNoExport(row.policies.media),
    checked(row.liability.participate),
    checked(row.liability.transport),
    checked(row.liability.risks),
    checked(row.liability.release),
  ];
}

export function permissionDetailLines(
  value: PermissionAnswers | undefined,
  locale: Locale,
): Array<{ label: string; value: string }> {
  if (!value || (!filled(value.student.address) && !filled(value.student.firstName))) return [];
  const en = locale !== "es";
  const label = (english: string, spanish: string) => (en ? english : spanish);
  const lines: Array<{ label: string; value: string }> = [];
  const push = (english: string, spanish: string, item: string) => {
    if (filled(item)) lines.push({ label: label(english, spanish), value: item.trim() });
  };
  push("Student phone", "Teléfono del estudiante", value.student.phone);
  push("Address", "Dirección", value.student.address);
  push("Gender", "Género", optionLabel(GENDERS, value.student.gender, locale));
  push(
    "Race / ethnicity",
    "Raza / etnia",
    value.student.races.map((id) => optionLabel(RACES, id, locale)).join(", "),
  );
  push("May leave with", "Puede salir con", value.student.pickupPeople);
  if (value.student.walkHome) {
    lines.push({
      label: label("Walk home", "Caminar a casa"),
      value: value.student.walkHome === "yes"
        ? label("Yes", "Sí")
        : label("No, pickup required", "No, requiere que lo recojan"),
    });
  }
  push(
    "Guardian relationship",
    "Parentesco del tutor",
    optionLabel(RELATIONSHIPS, value.guardian.relationship, locale),
  );
  push("Guardian address", "Dirección del tutor", value.guardian.address);
  push(
    "Help with",
    "Quiere ayudar con",
    value.guardian.help.map((id) => optionLabel(HELP_INTERESTS, id, locale)).join(", "),
  );
  const extraName = `${value.additionalGuardian.firstName} ${value.additionalGuardian.lastName}`.trim();
  push("Additional guardian", "Tutor adicional", extraName);
  push("Additional guardian email", "Correo del tutor adicional", value.additionalGuardian.email);
  push("Additional guardian phone", "Teléfono del tutor adicional", value.additionalGuardian.phone);
  push(
    "Additional guardian relationship",
    "Parentesco del tutor adicional",
    optionLabel(RELATIONSHIPS, value.additionalGuardian.relationship, locale),
  );
  push("Additional guardian address", "Dirección del tutor adicional", value.additionalGuardian.address);
  push(
    "Emergency relationship",
    "Parentesco del contacto de emergencia",
    value.emergency1.relationship,
  );
  const emergency2 = `${value.emergency2.firstName} ${value.emergency2.lastName}`.trim();
  push("Emergency contact 2", "Contacto de emergencia 2", emergency2);
  push("Emergency 2 phone", "Teléfono del contacto 2", value.emergency2.phone);
  push("Emergency 2 relationship", "Parentesco del contacto 2", value.emergency2.relationship);
  if (value.health.behavioral) {
    lines.push({
      label: label("Behavioral or emotional needs", "Necesidades conductuales o emocionales"),
      value: value.health.behavioral === "yes"
        ? value.health.behavioralDetail.trim() || label("Yes", "Sí")
        : label("None reported", "Ninguna reportada"),
    });
  }
  if (value.health.medical) {
    lines.push({
      label: label("Medical conditions", "Condiciones médicas"),
      value: value.health.medical === "yes"
        ? value.health.medicalDetail.trim() || label("Yes", "Sí")
        : label("None reported", "Ninguna reportada"),
    });
  }
  const yesNo = (
    english: string,
    spanish: string,
    choice: YesNo | "",
    yesText: [string, string] = ["Yes", "Sí"],
    noText: [string, string] = ["No", "No"],
  ) => {
    if (!choice) return;
    lines.push({
      label: label(english, spanish),
      value: choice === "yes" ? label(yesText[0], yesText[1]) : label(noText[0], noText[1]),
    });
  };
  const agreement = (english: string, spanish: string, choice: AgreeChoice | "") => {
    if (!choice) return;
    lines.push({
      label: label(english, spanish),
      value:
        choice === "agree"
          ? label("Agreed", "De acuerdo")
          : label("Meeting requested", "Solicita una reunión"),
    });
  };
  yesNo("Over-the-counter medication", "Medicamentos de venta libre", value.health.otcMeds);
  yesNo(
    "Emergency medical care",
    "Atención médica de emergencia",
    value.health.emergencyCare,
    ["Authorized", "Autorizada"],
    ["Not authorized", "No autorizada"],
  );
  push("Physician", "Médico", value.health.physicianName);
  push("Physician phone", "Teléfono del médico", value.health.physicianPhone);
  push("Preferred hospital", "Hospital preferido", value.health.hospital);
  push("Insurance", "Seguro médico", value.health.insuranceProvider);
  push("Policy number", "Número de póliza", value.health.policyNumber);
  agreement("Code of conduct", "Código de conducta", value.policies.conduct);
  agreement("Behavior response", "Respuesta a la conducta", value.policies.behavior);
  agreement("Dismissal policy", "Política de salida", value.policies.dismissal);
  yesNo("Media release", "Autorización de medios", value.policies.media);
  const showLiability =
    value.policies.media !== "" ||
    value.liability.participate ||
    value.liability.transport ||
    value.liability.risks ||
    value.liability.release;
  if (showLiability) {
    const flag = (english: string, spanish: string, on: boolean) => {
      lines.push({
        label: label(english, spanish),
        value: on ? label("Yes", "Sí") : label("No", "No"),
      });
    };
    flag("Participation permission", "Permiso de participación", value.liability.participate);
    flag("Transportation permission", "Permiso de transporte", value.liability.transport);
    flag("Assumption of risk", "Asunción de riesgo", value.liability.risks);
    flag("Liability release", "Exención de responsabilidad", value.liability.release);
  }
  return lines;
}

export function medicalSummary(
  permission: PermissionAnswers | undefined,
  legacyAuthorized?: boolean,
  locale: Locale = "en",
): string {
  const text = (english: string, spanish: string) => (locale === "es" ? spanish : english);
  const health = permission?.health;
  if (health?.medical === "yes" || health?.medical === "no") {
    const condition =
      health.medical === "yes"
        ? health.medicalDetail.trim() || text("Yes", "Sí")
        : text("None reported", "Ninguna reportada");
    if (health.emergencyCare === "no") {
      return `${condition} · ${text("No emergency care", "Sin atención de emergencia")}`;
    }
    return condition;
  }
  if (legacyAuthorized === true) {
    return text("Emergency care authorized", "Atención de emergencia autorizada");
  }
  if (legacyAuthorized === false) {
    return text("Emergency care not authorized", "Atención de emergencia no autorizada");
  }
  return "—";
}

export function mediaReleaseSummary(
  permission: PermissionAnswers | undefined,
  locale: Locale = "en",
): string {
  if (permission?.policies.media === "yes") return locale === "es" ? "Sí" : "Yes";
  if (permission?.policies.media === "no") return "No";
  return "—";
}
